/**
 * The mailbox's record page and its Grants panel (piece 32; decisions 0081,
 * 0790, 0794, 0840): every `PermissionGrant` naming the mailbox, open and
 * ended, and who may write one. 🔒 A mailbox grant is a grant with scope
 * Mailbox naming the `Mailbox` id, whatever the kind of mailbox, so the panel,
 * the gate and the access audit read one row shape.
 */
import { MAIL_API_PATH, MAIL_MAILBOXES_PATH } from './client';
import { isEmailAddress } from '../validators';
import type { DepartedReplyView } from './outOfOffice';
import type { MailboxHostModeValue, MailboxKindValue } from './model';

/** 🔒 The capabilities + Grant offers on a mailbox (decisions 0081, 0794). */
export const MAILBOX_GRANT_CAPABILITIES = ['Read', 'SendAs'] as const;
export type MailboxGrantCapability = (typeof MAILBOX_GRANT_CAPABILITIES)[number];

/** The mailbox's record page, opened from Settings › Email › Mailboxes and from the folder tree (decision 0840). */
export const MAIL_MAILBOX_ROUTE = '/mail/mailboxes/:mailboxId';
export const mailMailboxRoute = (mailboxId: string): string => `/mail/mailboxes/${mailboxId}`;

/** The API paths the record page reads and writes, relative to the API root. */
export const mailMailboxPath = (mailboxId: string): string => `${MAIL_MAILBOXES_PATH}/${mailboxId}`;
export const mailMailboxGrantsPath = (mailboxId: string): string => `${mailMailboxPath(mailboxId)}/grants`;
export const mailMailboxGrantEndPath = (mailboxId: string, grantId: string): string => `${mailMailboxGrantsPath(mailboxId)}/${grantId}/end`;
/** 🔒 A super admin's switch between watching and writing (decision 1206). */
export const mailMailboxHostModePath = (mailboxId: string): string => `${mailMailboxPath(mailboxId)}/host-mode`;
/** The people + Grant offers: every open employment at the Organization. */
export const MAIL_GRANTEES_PATH = `${MAIL_API_PATH}/grantees`;

/** 🔒 The Mail Settings window's Send As and Delegation page (T419; decision 1236). */
export const MAIL_DELEGATION_PATH = `${MAIL_API_PATH}/settings/delegation`;

/** One open grant the viewer holds on somebody else's or a shared mailbox. */
export interface HeldMailboxGrant {
  mailboxId: string;
  name: string;
  address: string;
  kind: 'Personal' | 'Department' | 'Module';
  capability: MailboxGrantCapability;
  /** The grant's last day, `YYYY-MM-DD`, or null for none. */
  endDate: string | null;
}

/** One open grant somebody holds on the viewer's own mailbox: a delegation. */
export interface GivenMailboxGrant {
  grantee: string;
  capability: MailboxGrantCapability;
  endDate: string | null;
}

export interface DelegationAnswer {
  /** The viewer's own Personal mailbox, whose record page is where a delegation is given and ended; null where they have none. */
  ownMailboxId: string | null;
  held: HeldMailboxGrant[];
  given: GivenMailboxGrant[];
}

/** The largest quota the edit takes, in megabytes: a petabyte, far past any mailbox and well inside the column's integer. */
export const MAILBOX_QUOTA_MAX_MEGABYTES = 1_000_000_000;

/** What is wrong with a Department mailbox's edit, in words, or null. */
export function mailboxEditProblem(input: Partial<MailboxEditInput>): string | null {
  if (!input.name || !input.name.trim()) return 'a mailbox has a name the folder tree prints';
  if (input.name.trim().length > 120) return 'a mailbox name is at most 120 characters';
  if (!input.address || !isEmailAddress(input.address.trim())) return 'the address is one email address';
  if (!input.departmentId) return 'a department mailbox names its department';
  // 🔒 T3424: a quota is a whole number of megabytes, or empty for none.
  if (input.quotaMegabytes !== undefined && input.quotaMegabytes !== null && (!Number.isInteger(input.quotaMegabytes) || input.quotaMegabytes < 1 || input.quotaMegabytes > MAILBOX_QUOTA_MAX_MEGABYTES)) {
    return `a quota is a whole number of megabytes from 1 to ${MAILBOX_QUOTA_MAX_MEGABYTES}, or empty for none`;
  }
  return null;
}

/** One grant as the panel prints it. */
export interface MailboxGrantRow {
  id: string;
  /** The version End names, so a grant another writer changed since is refused (piece 7). */
  version: number;
  employmentId: string;
  /** The grantee's name, through the employment to the person. */
  grantee: string;
  capability: string;
  reason: string;
  grantedBy: string;
  /** An instant, drawn in the viewer's zone. */
  grantedAt: string;
  /** The last day the grant reads, a calendar date in the organisation's zone; null runs open. */
  endDate: string | null;
  open: boolean;
  /** Whether this viewer may press End on it. */
  mayEnd: boolean;
}

/** The mailbox as its record page heads it. */
export interface MailboxRecordFacts {
  id: string;
  name: string;
  address: string;
  kind: MailboxKindValue;
  /** The owner in words: the person, the department or the module. */
  owner: string;
  isDistributionList: boolean;
  departed: boolean;
  quotaMegabytes: number | null;
  /** The owning department of a Department mailbox, which its record page edits (decision 0842). */
  departmentId: string | null;
  /** The version an edit names (piece 7). */
  version: number;
  /** 🔒 WatchOnly writes nothing to the host; Full writes flags and moves through (decision 1206). */
  hostMode: MailboxHostModeValue;
  /** The organisation's day the row was made, `YYYY-MM-DD`: what *Watching Only since* reads. */
  boundOn: string;
}

/** One member of a distribution list: from the department tree, read live, or added by a Read grant (decision 0794). */
export interface DistributionMemberRow {
  employmentId: string;
  name: string;
  /** The address the list expands to for this member; null where they answer at none and are skipped. */
  address: string | null;
  via: 'department' | 'grant';
}

export interface MailboxRecordAnswer {
  mailbox: MailboxRecordFacts;
  /** A distribution list's members as a send would expand it today; null on every other mailbox. */
  members: DistributionMemberRow[] | null;
  /** Open grants first, then ended ones, newest first within each. */
  grants: MailboxGrantRow[];
  /** What + Grant offers this viewer here; empty hides the button. */
  offers: MailboxGrantCapability[];
  /** The organisation's today, `YYYY-MM-DD`: the earliest last day + Grant accepts, and what End writes. */
  today: string;
  /** 🔒 A super admin edits a Department mailbox's name, address and department here (decision 0842). */
  mayEdit: boolean;
  /** 🔒 A super admin switches the mailbox between watching and writing (decision 1206). */
  mayChangeHostMode: boolean;
  /** The departments the edit offers, where `mayEdit`; empty otherwise. */
  departments: { id: string; name: string }[];
  /** 🔒 A departed Personal mailbox's automatic reply, which a super admin sets (decision 0843); null on every other mailbox. */
  departedReply: DepartedReplyView | null;
}

/** A Department mailbox's row as its record page edits it (decision 0842). */
export interface MailboxEditInput {
  name: string;
  address: string;
  departmentId: string;
  /** T3424: the quota in megabytes; null clears it, and absent leaves it as it stands. */
  quotaMegabytes?: number | null;
  version?: number;
}

export interface MailboxGrantInput {
  employmentId: string;
  capability: MailboxGrantCapability;
  reason: string;
  /** The grant's last day, `YYYY-MM-DD`; absent or null runs open. */
  endDate?: string | null;
}

/** One person + Grant offers. */
export interface MailGranteeOption {
  employmentId: string;
  name: string;
  address: string | null;
}

/** What decides who writes a mailbox's grants. */
export interface MailboxWriterFacts {
  kind: MailboxKindValue;
  departed: boolean;
  isDistributionList: boolean;
  /** The viewer owns this Personal mailbox. */
  viewerIsOwner: boolean;
  viewerIsSuperAdmin: boolean;
  /** The viewer heads the department that owns this Department mailbox (decision 0842). */
  viewerHeadsDepartment?: boolean;
}

/**
 * 🔒 The capabilities a viewer may grant on a mailbox (decisions 0794, 0840).
 * Delegation is the owner's or a super admin's; a Department or Module
 * mailbox's grants are a super admin's; a departed mailbox offers Read alone,
 * to a super admin; a distribution list's grants add members, so Read alone.
 * The head of a Department mailbox's department adds and removes its readers,
 * and its SendAs stays a super admin's (decision 0842).
 */
export function mailboxGrantOffers(facts: MailboxWriterFacts): MailboxGrantCapability[] {
  if (facts.departed) return facts.viewerIsSuperAdmin ? ['Read'] : [];
  if (facts.kind === 'Personal') return facts.viewerIsOwner || facts.viewerIsSuperAdmin ? ['Read', 'SendAs'] : [];
  if (facts.isDistributionList) return facts.viewerIsSuperAdmin || facts.viewerHeadsDepartment ? ['Read'] : [];
  if (facts.viewerIsSuperAdmin) return ['Read', 'SendAs'];
  return facts.kind === 'Department' && facts.viewerHeadsDepartment ? ['Read'] : [];
}

const DAY = /^\d{4}-\d{2}-\d{2}$/;

/** What is wrong with a + Grant, in words, or null: a person, an offered capability, a reason, and a last day no earlier than today. */
export function mailboxGrantProblem(input: Partial<MailboxGrantInput>, offers: readonly MailboxGrantCapability[], today: string): string | null {
  if (offers.length === 0) return 'granting on this mailbox is its owner’s or a super admin’s';
  if (!input.employmentId) return 'choose the person to grant';
  if (!input.capability || !offers.includes(input.capability)) return `this mailbox offers ${offers.join(' and ')}`;
  if (!input.reason || !input.reason.trim()) return 'a grant carries a reason, or it is unreviewable';
  if (input.endDate !== undefined && input.endDate !== null && input.endDate !== '') {
    if (!DAY.test(input.endDate) || Number.isNaN(Date.parse(`${input.endDate}T00:00:00Z`))) return 'the last day is a date';
    if (input.endDate < today) return 'the last day cannot be before today';
  }
  return null;
}

/**
 * 🔒 A distribution list's address expanded to its members at send time
 * (decisions 0079, 0794, 0841). The addresses the transport is handed have each
 * list replaced by its members; `resolved` is what the lists produced, which the
 * message keeps in `resolvedRecipients?` while `toAddresses` keeps the list as
 * typed. A member named twice is sent once.
 */
export function expandDistributionLists(addresses: readonly string[], members: ReadonlyMap<string, readonly string[]>): { addresses: string[]; resolved: string[] } {
  const out: string[] = [];
  const resolved: string[] = [];
  const push = (list: string[], address: string) => {
    const lower = address.trim().toLowerCase();
    if (lower && !list.includes(lower)) list.push(lower);
  };
  for (const address of addresses) {
    const expansion = members.get(address.trim().toLowerCase());
    if (!expansion) {
      push(out, address);
      continue;
    }
    for (const member of expansion) {
      push(out, member);
      push(resolved, member);
    }
  }
  return { addresses: out, resolved };
}
